import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.uuid();

export const CompanyListItemSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  originId: z.string().nullable(),
  shortName: z.string().nullable(),
  email: z.string().nullable(),
  lastUploadedAt: z.date().nullable(),
  /** True when the company has a ship-and-debit entity module config row. */
  shipAndDebitEnabled: z.boolean(),
  /**
   * Vendor numbers distinct from company code. For Rexel these are the
   * 5-digit AS/400 vendor#s (multiple allowed). The S&D / VENDMAST /
   * as400_rexel company that stores them is often a different row than the
   * pricelist company (originId SCH01); the list joins them by that code.
   */
  vendorNumbers: z.array(z.string()),
});

export const ListCompaniesInputSchema = z.object({
  page: z.number().int().positive().default(1),
  pageSize: z.number().int().positive().max(100).default(20),
  search: z.string().optional(),
  /**
   * Item Master pricelist management only. Collapses division-scoped
   * AS/400 vendor rows and excludes them from the unfiltered list.
   * Default false keeps EDI / shared pickers on stored company rows.
   */
  forPricelistManagement: z.boolean().default(false),
});

export const ListCompaniesResponseSchema = z.object({
  companies: z.array(CompanyListItemSchema),
  totalCount: z.number().int().nonnegative(),
});

export const CsvDocumentSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  createdAt: z.date(),
});

export const CompanyPricelistDocumentSchema = z.object({
  id: UUIDSchema,
  associationId: UUIDSchema,
  name: z.string(),
  mimeType: z.string().nullable(),
  fileSizeBytes: z.number().nullable(),
  createdAt: z.date(),
  uploadedByName: z.string().nullable(),
  /** Recorded on the pricelist's details, YYYY-MM-DD, null when unset. */
  effectiveDate: z.string().date().nullable(),
  erpUploadDate: z.string().date().nullable(),
  /**
   * The pricelist update case that owns this file, when one does — the case is
   * then the source of the dates above rather than the legacy details row.
   * Null for a pricelist nobody has made a case for.
   */
  caseId: UUIDSchema.nullable(),
  /** The owning case's readable number, e.g. `PC-1042`. */
  caseNumber: z.string().nullable(),
  csvDocuments: z.array(CsvDocumentSchema),
});

export const ListCompanyPricelistsInputSchema = z.object({
  companyId: z.uuid(),
  page: z.number().int().positive().default(1),
  pageSize: z.number().int().positive().max(100).default(10),
});

export const ListCompanyPricelistsResponseSchema = z.object({
  documents: z.array(CompanyPricelistDocumentSchema),
  totalCount: z.number().int().nonnegative(),
});

export const CompanyBasicInfoSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

export const CompaniesBySupplierIdsInputSchema = z.object({
  supplierIds: z.array(UUIDSchema).min(1).max(500),
});

// Response is a record mapping supplierId -> company basic info (or null if no company found)
export const CompaniesBySupplierIdsResponseSchema = z.record(
  z.string(),
  CompanyBasicInfoSchema.nullable(),
);

export const CompanyIdInputSchema = z.object({
  companyId: z.uuid(),
});

export const AddSupplierResponseSchema = z.object({
  id: z.uuid(),
});

export const AddCustomerResponseSchema = z.object({
  id: z.uuid(),
});

const CompanyActionSuccessSchema = z.object({
  success: z.literal(true),
});

export const CompanyDistinctValuesInputSchema = z.object({
  column: z.string(),
  search: z.string().optional(),
});

export const CompanyDistinctValuesResponseSchema = z.object({
  values: z.array(z.string()),
});

export const companiesContracts = {
  bySupplierIds: oc
    .input(CompaniesBySupplierIdsInputSchema)
    .output(CompaniesBySupplierIdsResponseSchema)
    .route({
      method: "GET",
      summary: "Get companies for suppliers",
      description:
        "Maps each given supplier ID (up to 500) to its linked company's ID and name, or null when the supplier has no company.",
    }),
  addSupplier: oc
    .input(CompanyIdInputSchema)
    .output(AddSupplierResponseSchema)
    .route({
      method: "POST",
      summary: "Make a company a supplier",
      description:
        "Creates an active supplier record linked to the company, copying its name and email. Fails if the company already has a supplier.",
    }),
  removeSupplier: oc
    .input(CompanyIdInputSchema)
    .output(CompanyActionSuccessSchema)
    .route({
      method: "POST",
      summary: "Remove a company's supplier record",
      description:
        "Deletes the supplier record linked to the company; the company itself remains. Fails if no supplier is linked.",
    }),
  addCustomer: oc
    .input(CompanyIdInputSchema)
    .output(AddCustomerResponseSchema)
    .route({
      method: "POST",
      summary: "Make a company a customer",
      description:
        "Creates a customer record linked to the company, copying its name. Fails if the company already has a customer.",
    }),
  removeCustomer: oc
    .input(CompanyIdInputSchema)
    .output(CompanyActionSuccessSchema)
    .route({
      method: "POST",
      summary: "Remove a company's customer record",
      description:
        "Deletes the customer record linked to the company; the company itself remains. Fails if no customer is linked.",
    }),
  distinctValues: oc
    .input(CompanyDistinctValuesInputSchema)
    .output(CompanyDistinctValuesResponseSchema)
    .route({
      method: "GET",
      summary: "Get distinct values for a company column",
      description:
        "Returns up to 500 distinct values of a company field (name, email, short name, or a metadata key) for filter dropdowns.",
    }),
  listCompanies: oc
    .input(ListCompaniesInputSchema)
    .output(ListCompaniesResponseSchema)
    .route({
      method: "GET",
      summary: "List companies",
      description:
        "Returns a paged, name-sorted list of the organization's companies with vendor numbers, last price list upload date, and ship-and-debit status; supports text search.",
    }),
  listCompanyPricelists: oc
    .input(ListCompanyPricelistsInputSchema)
    .output(ListCompanyPricelistsResponseSchema)
    .route({
      method: "GET",
      summary: "List a company's price lists",
      description:
        "Returns a paged list of price list files uploaded for one company, with effective and ERP upload dates, owning update case, and converted CSV files.",
    }),
} as const;

export type CompanyBasicInfo = z.infer<typeof CompanyBasicInfoSchema>;
export type CompaniesBySupplierIdsInput = z.infer<
  typeof CompaniesBySupplierIdsInputSchema
>;
export type CompaniesBySupplierIdsResponse = z.infer<
  typeof CompaniesBySupplierIdsResponseSchema
>;
export type CompanyDistinctValuesInput = z.infer<
  typeof CompanyDistinctValuesInputSchema
>;
export type CompanyListItem = z.infer<typeof CompanyListItemSchema>;
export type ListCompaniesInput = z.infer<typeof ListCompaniesInputSchema>;
export type ListCompaniesResponse = z.infer<typeof ListCompaniesResponseSchema>;
export type CsvDocument = z.infer<typeof CsvDocumentSchema>;
export type CompanyPricelistDocument = z.infer<
  typeof CompanyPricelistDocumentSchema
>;
export type ListCompanyPricelistsInput = z.infer<
  typeof ListCompanyPricelistsInputSchema
>;
export type ListCompanyPricelistsResponse = z.infer<
  typeof ListCompanyPricelistsResponseSchema
>;
